import React, { useRef, useEffect } from 'react';
import { Search, Bell, Upload, X, Check, AlertTriangle, XCircle, Info, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import { useApp } from '../../context/AppContext';

interface HeaderProps {
  title: string;
  subtitle?: string;
}

export const Header: React.FC<HeaderProps> = ({ title, subtitle }) => {
  const {
    filters, setFilters,
    setShowUploadModal,
    notifications, unreadCount, showNotifications, setShowNotifications,
    markNotificationRead, markAllNotificationsRead, navigateToDocument,
    showUserMenu, setShowUserMenu, setActiveView,
    user, logout,
  } = useApp();

  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [setShowNotifications, setShowUserMenu]);

  const notifIcon = (type: string) => {
    switch (type) {
      case 'critical': return <XCircle size={14} className="text-red-500" />;
      case 'warning': return <AlertTriangle size={14} className="text-amber-500" />;
      case 'success': return <Check size={14} className="text-emerald-500" />;
      case 'info': return <Info size={14} className="text-blue-500" />;
      default: return <Info size={14} className="text-blue-500" />;
    }
  };

  return (
    <header className="h-16 flex items-center justify-between px-8 border-b border-surface-100 bg-white/60 backdrop-blur-md sticky top-0 z-20">
      <div>
        <h1 className="text-lg font-semibold text-surface-900">{title}</h1>
        {subtitle && <p className="text-xs text-surface-400 mt-0.5">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            className="pl-9 pr-8 py-2 text-sm bg-surface-50 border border-surface-200 rounded-xl w-64 focus:outline-none focus:ring-2 focus:ring-brand-400/20 focus:border-brand-400 transition-all placeholder:text-surface-400"
          />
          {filters.search ? (
            <button
              onClick={() => setFilters({ search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
            >
              <X size={14} />
            </button>
          ) : (
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-surface-400 bg-surface-100 px-1.5 py-0.5 rounded">
              /
            </kbd>
          )}
        </div>

        {/* Upload Document */}
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-400 rounded-xl hover:bg-brand-500 transition-colors shadow-sm shadow-brand-400/20"
        >
          <Upload size={14} />
          <span>Upload Document</span>
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); }}
            className="relative p-2 text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-colors"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-bold text-white">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notification panel */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 glass-card shadow-xl overflow-hidden animate-fade-in">
              <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100">
                <span className="text-sm font-semibold text-surface-800">Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllNotificationsRead}
                    className="text-[11px] font-medium text-brand-400 hover:text-brand-500"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.map(n => (
                  <button
                    key={n.id}
                    onClick={() => {
                      markNotificationRead(n.id);
                      if (n.documentId) navigateToDocument(n.documentId);
                      setShowNotifications(false);
                    }}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-surface-50 transition-colors border-b border-surface-50 ${!n.read ? 'bg-brand-50/30' : ''
                      }`}
                  >
                    <div className="mt-0.5">{notifIcon(n.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-surface-800">{n.title}</span>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />}
                      </div>
                      <p className="text-[11px] text-surface-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-surface-400 mt-1">
                        {new Date(n.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* User Avatar/Menu */}
        <div className="relative" ref={userRef}>
          <button
            onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); }}
            className="flex items-center gap-2 ml-1"
          >
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-300 to-brand-400 flex items-center justify-center text-white text-xs font-bold">
              {user?.initials || 'U'}
            </div>
            <ChevronDown size={14} className="text-surface-400" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 glass-card shadow-xl overflow-hidden animate-fade-in">
              <div className="px-4 py-3 border-b border-surface-100">
                <p className="text-sm font-medium text-surface-800">{user?.name || 'User'}</p>
                <p className="text-xs text-surface-500">{user?.email || ''}</p>
              </div>
              <div className="py-1">
                <button
                  onClick={() => { setActiveView('settings'); setShowUserMenu(false); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-surface-600 hover:bg-surface-50 flex items-center gap-2.5 transition-colors"
                >
                  <Settings size={15} /> Settings
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm text-surface-600 hover:bg-surface-50 flex items-center gap-2.5 transition-colors">
                  <User size={15} /> Profile
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); logout(); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
                >
                  <LogOut size={15} /> Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
